import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import SearchableListPicker from "@core/components/SearchableListPicker";
import { useEntityScopeOptions } from "@features/entity/hooks/useEntityScopeOptions";
import {
    ENTITY_SCOPE_PICKER_VARIANT_CLASS,
    ENTITY_SCOPE_PICKER_WRAP_CLASS,
    ENTITY_SCOPE_SECTION_BG,
    ENTITY_SCOPE_SECTION_HEADERS,
    encodeEntityScopeDestination,
    formatEntityScopeLabel,
    parseEntityScopeDestination,
} from "@features/entity/constants/entityScopePicker";
import {
    getInviteDestinationChildScopes,
    getScopeFromValue,
} from "@features/invitations/utils/getScopeFromParam";

export interface EntityScopePickerChange {
    scope: string;
    scopeRefId: number;
    name?: string;
}

interface EntityScopePickerProps {
    ownScopeValue: string;
    ownScopeRefId: number;
    ownLabel: string;
    /** Encoded destination value (SCOPE:id). */
    value: string;
    onChange: (next: EntityScopePickerChange) => void;
    enabled?: boolean;
    isInvalid?: boolean;
    errorText?: string;
    /** When true, church-only workplaces show a read-only control. */
    readOnlyWhenNoChildren?: boolean;
    /** Extra class on the closed toggle (search-bar leading). */
    toggleClassName?: string;
    testId?: string;
    placeholder?: string;
}

const GROUP_TONE_BG: Record<string, string> = {
    [ENTITY_SCOPE_SECTION_HEADERS.NATION]: ENTITY_SCOPE_SECTION_BG.NATION,
    [ENTITY_SCOPE_SECTION_HEADERS.DISTRICT]: ENTITY_SCOPE_SECTION_BG.DISTRICT,
    [ENTITY_SCOPE_SECTION_HEADERS.CHURCH]: ENTITY_SCOPE_SECTION_BG.CHURCH,
};

/**
 * Searchable entity scope picker with colored nation / district / church sections.
 */
const EntityScopePicker: React.FC<EntityScopePickerProps> = ({
    ownScopeValue,
    ownScopeRefId,
    ownLabel,
    value,
    onChange,
    enabled = true,
    isInvalid = false,
    errorText,
    readOnlyWhenNoChildren = true,
    toggleClassName,
    testId = "entity-scope-picker",
    placeholder = "Select entity",
}) => {
    const ownScope = useMemo(() => getScopeFromValue(ownScopeValue), [ownScopeValue]);
    const childScopes = useMemo(
        () => getInviteDestinationChildScopes(ownScopeValue),
        [ownScopeValue]
    );
    const readOnly = readOnlyWhenNoChildren && childScopes.length === 0;

    const formattedOwnLabel = useMemo(() => {
        if (!ownScope) return ownLabel;
        if (/^(Nation|District|Church):/i.test(ownLabel.trim())) return ownLabel.trim();
        return formatEntityScopeLabel(ownScope, ownLabel);
    }, [ownScope, ownLabel]);

    const { options, loading } = useEntityScopeOptions(
        ownScopeValue,
        ownScopeRefId,
        formattedOwnLabel,
        enabled && !readOnly,
        false
    );

    const ownEncoded =
        ownScope && ownScopeRefId > 0
            ? encodeEntityScopeDestination(ownScope, ownScopeRefId)
            : "";

    const control = readOnly ? (
        <Form.Control
            type="text"
            value={formattedOwnLabel}
            disabled
            data-testid={`${testId}-readonly`}
        />
    ) : (
        <SearchableListPicker
            options={options}
            value={value || ownEncoded}
            onChange={(nextValue) => {
                const parsed = parseEntityScopeDestination(nextValue);
                if (!parsed) return;
                const opt = options.find((o) => String(o.value) === String(nextValue));
                onChange({
                    ...parsed,
                    name: opt?.label,
                });
            }}
            placeholder={placeholder}
            searchPlaceholder="Search…"
            loading={loading}
            isInvalid={isInvalid}
            groupToneBg={GROUP_TONE_BG}
            menuClassName={ENTITY_SCOPE_PICKER_VARIANT_CLASS}
            italicSublabel
            toggleClassName={toggleClassName}
            testId={testId}
        />
    );

    return (
        <div className={ENTITY_SCOPE_PICKER_WRAP_CLASS} data-testid={`${testId}-wrap`}>
            {control}
            {errorText && (
                <div className="text-danger small mt-1" data-testid={`${testId}-error`}>
                    {errorText}
                </div>
            )}
        </div>
    );
};

export default EntityScopePicker;
